import { useCallback, useEffect, useRef, useState } from "react";
import { apiErrorMessage } from "../../api/taskBoardService";

// Wait for edits to settle before regenerating, so a burst of task changes uploads once.
const AUTO_REGENERATE_DELAY_MS = 4000;

/**
 * Keeps a sprint's saved PDF (plan, timeline) in step with the task data.
 *
 * `hash` / `document` come from the API (`document.stale` = the data no longer
 * matches the PDF). For admins a missing or outdated PDF is regenerated
 * automatically — once per distinct data version, after edits settle, never
 * while another run is in flight. A failed attempt is not repeated until the
 * data changes or `regenerate` (Retry) is called.
 *
 * `sync()` builds, uploads and saves the PDF and resolves to the updated sprint.
 */
const useSprintDocument = ({ admin, hash, document, sync, onSaved, onDone, showError, showSuccess, successMessage }) => {
  const [busy, setBusy] = useState(false);
  const [genError, setGenError] = useState("");
  const busyRef = useRef(false);
  const attemptedHash = useRef(null);
  const latest = useRef({});
  latest.current = { sync, onSaved, onDone, showError, showSuccess };

  const run = useCallback(async (auto) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setGenError("");
    try {
      const updated = await latest.current.sync();
      latest.current.onSaved(updated);
      if (!auto) latest.current.showSuccess(successMessage);
      await latest.current.onDone?.();
    } catch (err) {
      const message = apiErrorMessage(err, "Could not generate the PDF");
      setGenError(message);
      if (!auto) latest.current.showError(message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [successMessage]);

  const needsPdf = Boolean(admin && hash && (!document || document.stale));
  useEffect(() => {
    if (!needsPdf || attemptedHash.current === hash) return undefined;
    const timer = setTimeout(() => {
      attemptedHash.current = hash;
      run(true);
    }, document ? AUTO_REGENERATE_DELAY_MS : 0);
    return () => clearTimeout(timer);
  }, [needsPdf, hash]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = () => {
    attemptedHash.current = hash ?? null;
    run(false);
  };

  return { busy, genError, regenerate };
};

export default useSprintDocument;
